import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

import CustomHead from '@components/CustomHead';
import Footer from '@components/FooterNew';
import Header from '@components/HeaderNew';
import Heading from '@components/Heading';
import HeroBottom from '@components/HeroBottom';
import Section from '@components/Section';
import { getDbtTutorialPosts } from '@utils/contentfulClient';

const Tutorials = ({ data }) => {
  const icon = `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/features/dbt/dbt_tutorial_icon.svg`;
  const sections = Object.keys(data);

  return (
    <div>
      {sections.map((type, index) => (
        <div key={index} className={`${data[type]?.length < 1 && 'hidden'}`}>
          <Heading Tag="h2" size="xs" className="mt-6 md:mt-12">
            {type}
          </Heading>
          <ul className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-10 mt-8">
            {data[type]?.map((tutorial) => (
              <li key={tutorial.sys.id}>
                <Link href={tutorial.fields.slug} as={tutorial.fields.slug}>
                  <a
                    className={`block mb-2 font-inter bg-white shadow-features h-[170px] sm:h-[200px] rounded-xl text-black font-medium text-lg flex px-6 sm:px-0`}
                  >
                    <div
                      className="w-[1px] ml-12 mr-8 hidden sm:block"
                      style={{ background: 'rgba(0, 0, 0, 0.05)' }}
                    ></div>
                    <div className="flex flex-col items-baseline justify-between py-5 sm:py-8">
                      <Image src={icon} alt="dbt icon" height="50" width="50" />
                      {tutorial.fields.title}
                    </div>
                  </a>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const LearnSqlPage = ({ tutorialsWithSections }) => {
  return (
    <>
      <CustomHead
        meta={{
          title: 'dbt Tutorials - PopSQL',
          description:
            'Elevate your data transformation skills with our extensive dbt tutorials. Learn how to use essential dbt commands, debug projects, manage data models, and create project documentation. An invaluable resource for analysts and engineers mastering dbt.',
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/og/dbt-tutorials.jpg`,
        }}
        openGraph={{
          title: 'dbt Tutorials - PopSQL',
          description:
            'Elevate your data transformation skills with our extensive dbt tutorials. Learn how to use essential dbt commands, debug projects, manage data models, and create project documentation. An invaluable resource for analysts and engineers mastering dbt.',
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/og/dbt-tutorials.jpg`,
        }}
      />
      <main>
        <Header />
        <Section
          customStyles={{
            background: `url(${process.env.NEXT_PUBLIC_ASSET_HOST}/static/homepage/home_hero_bg.svg)`,
            backgroundSize: '90%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'top',
          }}
        >
          <Heading Tag="h1" className="lg:text-[64px] mt-2 sm:mt-4">
            dbt Core
            <span className="text-xs ml-2 relative -top-8">TM</span>
          </Heading>

          <div className="w-full mt-2">
            <p className="text-2xl font-medium max-w-md text-center mx-auto mb-12 md:mb-20">
              Learn how to leverage dbt for your team with simple tutorials.
            </p>
            <Tutorials data={tutorialsWithSections} />
          </div>

          <HeroBottom
            icon={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/homepage/popsql_app_icon.png`}
            title="The most powerful workspace for data teams"
            description=""
          />
        </Section>
      </main>
      <Footer />
    </>
  );
};

export async function getStaticProps() {
  try {
    const dbtTutorials = await getDbtTutorialPosts();
    const tutorialsWithSections = {
      Basics: [],
      'Building projects': [],
      Integration: [],
      Deployment: [],
      Github: [],
    };

    // sort tutorials into each category
    dbtTutorials.forEach((tut) => {
      const types = tut.fields.type;
      types.forEach((type) => tutorialsWithSections[type]?.push(tut));
    });

    return {
      props: {
        tutorialsWithSections,
      },
    };
  } catch (error) {
    console.error(error);
    return {
      props: {
        err: 'Something went wrong 😕',
      },
    };
  }
}

export default LearnSqlPage;
